import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../context/LanguageContext';
import SEO from '../components/SEO';
import { PAGE_SEO } from '../utils/pageSeo';
import '../styles/aiAgents.css';
const analystImg = '/Images/agents/ai-analyst.jpg';
const predictiveImg = '/Images/agents/predictive-intelligent-agent.jpg';
const builderImg = '/Images/agents/ai-builder-dashboard-studio.jpg';
const liveDataImg = '/Images/agents/E1-DataBridge.jpg';
const governanceImg = '/Images/agents/ai-governance.jpg';

const AIAgents = () => {
  const { t } = useTranslation('aiAgents');
  const { language } = useLanguage();
  const seo = PAGE_SEO.aiAgents;

  return (
    <div className="ai-agents-page">
      <SEO 
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        canonicalPath={`/${language}/ai-agents`}
      />
      {/* Hero Section */}
      <section className="hero-section ai-agents-hero">
        <div className="hero-background">
        <div className="hero-bg-image"></div>
          {/*<div className="gradient-layer"></div>
          <div className="particle-layer"></div> */}
          <div className="grid-overlay"></div>
          {/* <div className="tech-lines"></div> */}
          <div className="moving-pipes">
            <div className="pipe pipe-1"></div>
            <div className="pipe pipe-2"></div>
            <div className="pipe pipe-3"></div>
            <div className="pipe pipe-4"></div>
            <div className="pipe pipe-5"></div>
            <div className="pipe pipe-6"></div>
            <div className="pipe pipe-7"></div>
            <div className="pipe pipe-8"></div>
            <div className="pipe pipe-9"></div>
            <div className="pipe pipe-10"></div>
            <div className="pipe pipe-11"></div>
            <div className="pipe pipe-12"></div>
            <div className="pipe pipe-13"></div>
            <div className="pipe pipe-14"></div>
            <div className="pipe pipe-15"></div>
          </div>
        </div>
        
        <div className="hero-content">
          <div className="container">
            <div className="hero-wrapper">
              <div className="tech-element tech-element-2"></div>
              <div className="tech-element tech-element-6"></div>
              <h1 className="hero-quote">
                <span className="quote-highlight">{t('hero.title')}</span><br />
                {t('hero.subtitle1')}<br />
                {t('hero.subtitle2')}
              </h1>
              <p className="hero-subtitle fade-in" style={{ animationDelay: '0.2s' }}>
                {t('hero.description')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Agents Content (Feature Highlights) */}
      <section className="ai-agents-content">
        <div className="container">
          <div className="agents-intro">
            <div className="agents-intro-centered fade-in">
              <div className="card-tag">{t('intro.tag')}</div>
              <h2 className="intro-heading">{t('intro.heading')}</h2>
              <p className="intro-description">
                {t('intro.description1')}
              </p>
              <p className="intro-description">
                {t('intro.description2')}
              </p>
              <div className="scroll-indicator">
                <span className="scroll-text">{t('intro.scrollText')}</span>
                <span className="scroll-arrow">↓</span>
              </div>
            </div>
          </div>
        </div>

        <div className="agents-highlights">
          {/* 1) AI Analyst */}
          <div className="agents-highlight tone-a">
            <div className="container">
              <div className="row g-5 align-items-center">
                <div className="col-lg-6">
                  <div className="card-tag">{t('analyst.tag')}</div>
                  <div className="highlight-title">{t('analyst.title')}</div>
                  <p className="highlight-lede">
                    {t('analyst.lede')}
                  </p>
                  <ul className="agents-points">
                    <li>{t('analyst.point1')}</li>
                    <li>{t('analyst.point2')}</li>
                    <li>{t('analyst.point3')}</li>
                  </ul>
                </div>
                <div className="col-lg-6">
                  <div className="agents-media">
                    <img src={analystImg} alt={t('analyst.title')} />
                    {/* <div className="ai-generated-overlay">AI-Generated</div> */}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 2) Predictive / Mapping */}
          <div className="agents-highlight tone-b">
            <div className="container">
              <div className="row g-5 align-items-center">
                <div className="col-lg-6 order-lg-2">
                  <div className="card-tag">{t('predictive.tag')}</div>
                  <div className="highlight-title">{t('predictive.title')}</div>
                  <p className="highlight-lede">
                    {t('predictive.lede')}
                  </p>
                  <ul className="agents-points">
                    <li>{t('predictive.point1')}</li>
                    <li>{t('predictive.point2')}</li>
                    <li>{t('predictive.point3')}</li>
                  </ul>
                </div>
                <div className="col-lg-6 order-lg-1">
                  <div className="agents-media">
                    <img src={predictiveImg} alt={t('predictive.title')} />
                    {/* <div className="ai-generated-overlay">AI-Generated</div> */}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 3) AI Builder + Dashboard Builder (merged) */}
          <div className="agents-highlight tone-c">
            <div className="container">
              <div className="row g-5 align-items-center">
                <div className="col-lg-6">
                  <div className="card-tag">{t('builder.tag')}</div>
                  <div className="highlight-title">{t('builder.title')}</div>
                  <p className="highlight-lede">
                    {t('builder.lede')}
                  </p>
                  <ul className="agents-points">
                    <li>{t('builder.point1')}</li>
                    <li>{t('builder.point2')}</li>
                    <li>{t('builder.point3')}</li>
                  </ul>
                </div>
                <div className="col-lg-6">
                  <div className="agents-media">
                    <img src={builderImg} alt={t('builder.title')} />
                    {/* <div className="ai-generated-overlay">AI-Generated</div> */}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 4) Live Data Crawler */}
          <div className="agents-highlight tone-a">
            <div className="container">
              <div className="row g-5 align-items-center">
                <div className="col-lg-6 order-lg-2">
                  <div className="card-tag">{t('crawler.tag')}</div>
                  <div className="highlight-title">{t('crawler.title')}</div>
                  <p className="highlight-lede">
                    {t('crawler.lede')}
                  </p>
                  <ul className="agents-points">
                    <li>{t('crawler.point1')}</li>
                    <li>{t('crawler.point2')}</li>
                    <li>{t('crawler.point3')}</li>
                  </ul>
                </div>
                <div className="col-lg-6 order-lg-1">
                  <div className="agents-media">
                    <img src={liveDataImg} alt={t('crawler.title')} />
                    {/* <div className="ai-generated-overlay">AI-Generated</div> */}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 5) Governance Link — stacked layout for larger infographic */}
          <div className="agents-highlight tone-c agents-highlight--governance">
            <div className="container">
              <div className="agents-governance-content">
                <div className="card-tag">{t('governance.tag')}</div>
                <div className="highlight-title">{t('governance.title')} <br /><small>{t('governance.subtitle')}</small></div>
                <p className="highlight-lede">
                  {t('governance.lede')}
                </p>
                <ul className="agents-points">
                  <li>{t('governance.point1')}</li>
                  <li>{t('governance.point2')}</li>
                  <li>{t('governance.point3')}</li>
                </ul>
                <div className="governance-cta-wrap mt-4">
                  <Link to={`/${language}/ai-governance`} className="hero-cta" style={{ marginTop: 0 }}>
                    {t('governance.cta')}
                    <span className="cta-chevrons">
                      <span>›</span><span>›</span><span>›</span>
                    </span>
                  </Link>
                </div>
              </div>
              <div className="agents-media agents-media--governance">
                <img src={governanceImg} alt={t('governance.title')} />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AIAgents;
